import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import * as Analytics from 'analytics/analytics'
import { selectPackageUpgradeOptionToCart } from 'analytics/snowplow/events'
import { getIsLuxPlusToursEnabled, getIsPostPurchaseTourChangeDates } from 'checkout/selectors/tourV2Selectors'
import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import Modal from 'LuxKit/Modal/Modal'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowSale from 'LuxKit/PricePoints/PriceRowSale'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TourV2DepartureCta from 'components/OfferPage/TourV2/Departures/TourV2DepartureCta'
import TourV2ItemInclusions from 'components/OfferPage/TourV2/TourV2ItemInclusions'
import Group from 'components/utils/Group'
import InlineBulletList from 'components/utils/InlineBulletList'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import GeoContext from 'contexts/geoContext'
import ModalContext from 'contexts/ModalContext'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import { sum } from 'lib/array/arrayUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { formatRoomType, getPackageOptionDisplayText, getTourV2SavingsWithLuxPlus } from 'lib/tours/tourUtils'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import cn from 'clsx'
import styled from 'styled-components'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import {
  generateLoyaltyPointsCalculatorTourOptions,
} from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'

const Root = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  align-items: stretch;
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  box-shadow: inset 0 0 0 1px ${props => props.theme.palette.neutral.default.six};
  display: grid;
  grid-template:
    "details" 1fr
    "pricing" min-content
    / 1fr;
  gap: ${rem(12)};
  padding: ${rem(20)};
  position: relative;
  transition: .2s ease background, .2s ease box-shadow, .2s ease border;

  &.is-selected {
    border: 1px solid ${props => props.theme.palette.neutral.default.two};
  }

  &:not(.is-selected) {
    ${mediaHoverable} {
      &:hover {
        box-shadow: inset 0 0 0 1px ${props => props.theme.palette.neutral.default.six},
          ${props => props.theme.shadow.bottom.small};
      }
    }
  }
`

const DetailsContainer = styled.div`
  flex-grow: 1;
  grid-area: details;
`

const PricingContainer = styled(Group)`
  grid-area: pricing;
`

const SelectedButton = styled(TextButton)`
  cursor: unset;
`

interface Props {
  tourOffer: App.Tours.TourV2Offer
  tourItems: Array<App.Checkout.TourV2Item>
  packageOption: App.Tours.PackageUpgrade
  packageOptionIndex: number
  purchasableOption: App.Tours.TourV2OfferPurchasableOption
  purchasableOptionBase?: App.Tours.TourV2OfferPurchasableOption
  purchasableOptionWithPriceBreakdown: Array<[App.Tours.TourV2OfferPurchasableOption, App.Tours.V2OfferDeparturePriceBreakdown]>
  isSelected: boolean
  onPackageOptionSelect: (departure: App.Tours.TourV2OfferPurchasableOption) => void
  roomTypeMissing?: string | null
  contact: App.GeoContact
  isInventoryAvailable: boolean
  trackAnalytics?: boolean
}

function TourV2PackageOptionDetailsTile(props: Props) {
  const {
    packageOption,
    purchasableOption,
    onPackageOptionSelect,
    purchasableOptionWithPriceBreakdown,
    isSelected,
    roomTypeMissing,
    contact,
    tourOffer,
    tourItems,
    isInventoryAvailable,
    trackAnalytics = true,
  } = props
  const { inclusions, packageOption: packageOptionName } = packageOption
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const isTourDateChange = useAppSelector(getIsPostPurchaseTourChangeDates)
  const showModal = useContext(ModalContext)

  const {
    pricePerPerson,
    memberPricePerPerson,
    hasDiscount,
    fullPricePerPerson,
    memberPrice,
  } = useTourV2PurchasableOptionDetails(purchasableOption) || {}

  const totalPrice = useMemo(() => {
    return sum(purchasableOptionWithPriceBreakdown, ([, priceBreakDown]) => priceBreakDown.totalPrice)
  }, [purchasableOptionWithPriceBreakdown])

  const totalDiffPrice = useMemo(() => {
    return isTourDateChange && tourItems?.[0]?.originalTotal ? (totalPrice - tourItems[0].originalTotal) > 0 ? totalPrice - tourItems[0].originalTotal : 0 : undefined
  }, [isTourDateChange, tourItems, totalPrice])

  const handleDateSelectButtonClick = useCallback((_e: React.MouseEvent<HTMLButtonElement>) => {
    onPackageOptionSelect(purchasableOption)
    if (trackAnalytics) {
      Analytics.trackEvent(selectPackageUpgradeOptionToCart(
        tourOffer,
        packageOptionName,
        AnalyticsPage.checkout,
        currentRegionCode,
        currentCurrency,
        tourItems,
      ))
    }
  }, [onPackageOptionSelect, purchasableOption, tourOffer, packageOptionName, currentRegionCode, currentCurrency, tourItems, trackAnalytics])

  const { inclusionsToBeDisplayed, inclusionsLeft } = useMemo(() => {
    if (inclusions?.length > 0) {
      return {
        inclusionsToBeDisplayed: inclusions.slice(0, 5),
        inclusionsLeft: inclusions.length - 5,
      }
    }
    return { inclusionsToBeDisplayed: [], inclusionsLeft: 0 }
  }, [inclusions])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return [generateLoyaltyPointsCalculatorTourOptions(
      tourOffer,
      {
        price: pricePerPerson,
        memberPrice: memberPricePerPerson,
        roomType: purchasableOption.roomType,
        packageId: purchasableOption.fkDepartureId,
        offerId: purchasableOption.fkTourId,
      }),
    ]
  }, [tourOffer, pricePerPerson, memberPricePerPerson, purchasableOption.roomType, purchasableOption.fkDepartureId, purchasableOption.fkTourId])

  const showAllInclusions = useCallback(() => {
    showModal(<Modal title="Package inclusions">
      <TourV2ItemInclusions inclusions={inclusions} />
    </Modal>)
  }, [showModal, inclusions])

  const spotsLeft = purchasableOption.inventoryLeft
  const savingsWithLuxPlus = getTourV2SavingsWithLuxPlus(purchasableOption)
  const isLuxPlusToursAvailable = useAppSelector(state => getIsLuxPlusToursEnabled(state))
  const variation = tourOffer.variations[purchasableOption.fkVariationId]
  const startLocation = useMemo(() => variation.startLocation ? [variation.startLocation] : undefined, [variation])
  const travellers = useMemo(() => tourItems.map(item => item.occupancy), [tourItems])

  return <ProductPaletteProvider palette="default">
    <Root className={cn({ 'is-selected': isSelected })}>
      <DetailsContainer>
        <Group direction="vertical" gap={12}>
          <StructureLabel variant="standard">
            {packageOption?.packageOptionDisplayName || getPackageOptionDisplayText(packageOptionName, tourOffer.id)}
          </StructureLabel>

          <LabelGroup>
            {!roomTypeMissing && isInventoryAvailable && spotsLeft > 0 && spotsLeft < 5 && (
              <UrgencyLabel variant="critical2">{`${pluralizeToString('spot', spotsLeft)} left`}</UrgencyLabel>
            )}
            {isLuxPlusToursAvailable && !!memberPrice && <LuxPlusLabelWithModal
              offer={tourOffer}
              saveAmount={Number(savingsWithLuxPlus)}
              type="member-price-offer-tour"
            />}
          </LabelGroup>

          {packageOption.inclusions.length > 0 && <Group direction="vertical" gap={8}>
            <Caption variant="small" weight="bold">What's included</Caption>
            <TourV2ItemInclusions showHighlight inclusions={inclusionsToBeDisplayed} />
            {inclusionsLeft > 0 && (
              <TextLink size="medium" onClick={showAllInclusions}>+ {inclusionsLeft} more</TextLink>
            )}
          </Group>}
        </Group>
      </DetailsContainer>

      <PricingContainer direction="vertical">
        {roomTypeMissing && <Group direction="vertical" gap={12}>
          <Group direction="vertical" gap={4}>
            <BodyText variant="large" weight="bold">{roomTypeMissing} unavailable</BodyText>
            <BodyText variant="medium">Give our team a call for more package options.</BodyText>
          </Group>
          <TextButton
            fit="mobile-full-width"
            size="medium"
            kind="secondary"
            href={`tel:${contact.number}`}
          >
            Call on {contact.humanReadable}
          </TextButton>
        </Group>}

        {!roomTypeMissing && <Group
          direction="vertical"
          horizontalAlign="stretch"
          desktopDirection="horizontal"
          desktopHorizontalAlign="space-between"
          desktopVerticalAlign="end"
          gap={24}
        >
          <VerticalSpacer gap={4}>
            {!isTourDateChange && <div>
              <PriceRowPriceCaption>From</PriceRowPriceCaption>
              <LuxPlusPriceStack size="L" price={Number(pricePerPerson)} saleUnit="person" memberPrice={Number(memberPrice)} isTour />
              {hasDiscount && <PriceRowSale value={fullPricePerPerson!} />}
              <PriceRowTaxesAndFees />
              <PriceRowCaption>
                <InlineBulletList>
                  <li>
                    <FormatCurrency value={totalPrice} />&nbsp;Total
                  </li>
                  {purchasableOptionWithPriceBreakdown.length === 1 && purchasableOption.roomType && <li>
                    {formatRoomType(purchasableOption.roomType)}
                  </li>}
                </InlineBulletList>
              </PriceRowCaption>
              <PriceRowAgentHubCommission
                offerId={tourOffer.id}
                offerLocations={startLocation}
                productType={tourOffer.productType}
                offerTotal={totalPrice}
                optionId={purchasableOption.fkDepartureId}
              />
              <LoyaltyPointsEarn
                calculationRequests={pointsEarnCalculationRequests}
                calculationType="estimate"
              />
            </div>}

            {isTourDateChange && <>
              <LuxPlusPriceStack size="L" signDisplay="always" price={Number(totalDiffPrice)} saleUnit="total" isTour />
              <PriceRowTaxesAndFees />
              <PriceRowCaption>
                {travellers.length === 1 && purchasableOption && <>{formatRoomType(purchasableOption.roomType)}</>}
              </PriceRowCaption>
              <LoyaltyPointsEarn
                calculationRequests={pointsEarnCalculationRequests}
                calculationType="estimate"
              />
            </>}
          </VerticalSpacer>

          {!isInventoryAvailable && <TourV2DepartureCta
            variant="large"
            type="limited"
            offer={tourOffer}
            hasPrivateRequest={false}
            isPackageOptionTile
            travellers={travellers}
          />}

          {isInventoryAvailable && <>
            {isSelected && <ProductPaletteProvider product={tourOffer}>
              <SelectedButton fit="mobile-full-width" kind="primary" variant="dark" nonInteractable tabIndex={-1}>
                Selected
              </SelectedButton>
            </ProductPaletteProvider>}
            {!isSelected && <TextButton
              fit="mobile-full-width"
              kind="secondary"
              onClick={handleDateSelectButtonClick}
            >
              Select
            </TextButton>}
          </>}
        </Group>}
      </PricingContainer>
    </Root>
  </ProductPaletteProvider>
}

export default TourV2PackageOptionDetailsTile
